import React from 'react';
import _ from 'lodash';
import { daysAgo, handleAdminError } from '../../common/SharedFunctions';
import store, { brokerActions } from '@apply/adminStore';
import { Accordion, SortableHeader } from '../../common/SharedComponents';
import { BrokerApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { RefreshEvents } from '../../common/SharedEvents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTrashAlt, faUser } from '@fortawesome/free-regular-svg-icons';
import { faFlag } from '@fortawesome/free-solid-svg-icons';

const activeColumns = [
    { key: 'Status', label: 'Status', id: 'brokerStatus' },
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Name', label: 'Name' },
    { key: 'Email', label: 'Email', id: 'emailAddress' },
    { key: 'Modified', label: 'Modified', id: 'updatedAt' },
    {
        key: 'Point of Contact',
        label: 'Point of Contact',
        id: 'pointOfContact',
    },
];

const DraftBrokerTable = () => {
    const searchState = useSelector((state) => state.searchState);
    const brokerApi = new BrokerApi.BrokerApi(applicationInsights);

    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [emailBrokerId, set_EmailBrokerId] = React.useState(null);
    const [openSendEmailConfirm, set_openSendEmailConfirm] = React.useState(false);

    const [deleteBrokerId, set_deleteBrokerId] = React.useState(null);
    const [openDeleteBrokerConfirm, set_openDeleteBrokerConfirm] = React.useState(false);

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'brokerStatus',
        direction: 'asc',
    });

    React.useEffect(() => {
        _loadBrokers();

        refreshEvents.addRefreshListener(_loadBrokers);
    }, []);

    const _handleRowClick = (row) => {
        dispatch(brokerActions.setBrokerId({ brokerId: row.id }));
        dispatch(brokerActions.setBrokerModalOpen({ openBrokerModal: true }));
    };

    const _handleSendEmailButton = (brokerId) => {
        set_EmailBrokerId(brokerId);

        set_openSendEmailConfirm(true);
    };

    const _handleSendEmailConfirm = async () => {
        set_openSendEmailConfirm(false);

        await brokerApi.sendBrokerActivationEmail(emailBrokerId);
        refreshEvents.dispatchRefreshEvent();
    };

    const _handleDeleteBrokerButton = (brokerId) => {
        set_deleteBrokerId(brokerId);

        set_openDeleteBrokerConfirm(true);
    };

    const _handleDeleteBrokerConfirm = async () => {
        set_openDeleteBrokerConfirm(false);

        try {
            const response = await brokerApi.deleteBroker(deleteBrokerId);
            if (response.statusCode === 200) {
                refreshEvents.dispatchRefreshEvent();
            } else {
                throw new Error('An unexpected Error has occurred! Please try again.');
            }
        } catch (ex) {
            handleAdminError(ex, ex.message);
        }
    };

    const _loadBrokers = async () => {
        const response = await brokerApi.getDraftBrokerGridRows();

        if (response.statusCode !== 200) return;

        set_rows(
            response.result.map((b) => ({
                ...b,
                name: b.lastName + ', ' + b.firstName,
                updatedDaysAgo: daysAgo(new Date(b.updatedAt)),
            }))
        );
    };

    const matchName = (s) => s.name && s.name.toLowerCase().includes(searchState.searchText.broker.toLowerCase());
    const matchEmail = (s) =>
        s.emailAddress && s.emailAddress.toLowerCase().includes(searchState.searchText.broker.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter((s) => !searchState.searchText.broker || matchName(s) || matchEmail(s))
        .orderBy(sort.column, sort.direction)
        .value();

    return (
        <Accordion title='Draft (waiting on BFI)'>
            <div className='card'>
                <div className='card-section'>
                    <table>
                        <thead>
                            <tr>
                                <th className='count' />
                                {activeColumns.map((col) => (
                                    <SortableHeader
                                        key={col.key}
                                        column={col}
                                        sort={sort}
                                        onPress={set_sort}
                                    />
                                ))}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row) => (
                                <tr key={row.id}>
                                    <td />
                                    <td
                                        className='status'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className='icon'
                                        />{' '}
                                        Draft
                                    </td>
                                    <td
                                        className='isFlagged'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.isFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='Broker is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='primary nameColumn'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.name}
                                    </td>
                                    <td
                                        className='emailColumn'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.emailAddress}
                                    </td>
                                    <td
                                        className='time-stamp'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.updatedDaysAgo}
                                    </td>
                                    <td onClick={() => _handleRowClick(row)}>{row.pointOfContact}</td>
                                    <td className='actions'>
                                        <button
                                            className='gridButton'
                                            onClick={() => _handleSendEmailButton(row.id)}
                                        >
                                            <FontAwesomeIcon icon={faPaperPlane} /> Invite
                                        </button>
                                        <button
                                            className='gridRedButton'
                                            onClick={() => _handleDeleteBrokerButton(row.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <ConfirmModal
                isOpen={openSendEmailConfirm}
                text='Send an activation email to this user?'
                confirmCallback={_handleSendEmailConfirm}
                cancelCallback={() => set_openSendEmailConfirm(false)}
            />
            <ConfirmModal
                isOpen={openDeleteBrokerConfirm}
                text='Delete this broker?'
                confirmCallback={_handleDeleteBrokerConfirm}
                cancelCallback={() => set_openDeleteBrokerConfirm(false)}
            />
        </Accordion>
    );
};

export default DraftBrokerTable;
